/**
 * PostModule — the post-processing stack (Graphics G4).
 *
 * Configures the engine's PostProcessingPipeline (AA + bloom + SSAO + DoF) via
 * `app.renderer.createPostProcessing(scene, camera, config)` — the renderer applies it
 * during the Render phase when enabled, so this Post-phase module's job is to BUILD the
 * pipeline (lazily, once a camera exists) and switch quality tiers. AA runs in the post
 * chain (MSAA is off), so SSAO is safe (the r182 GTAO+MSAA WGSL gotcha is sidestepped).
 * ACES (G3) stays the final tone map — the post chain runs in HDR before it.
 */
import type * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { AutoExposureController } from 'enginex/render/post/AutoExposure.js';
import { exposureState, lightFrame, E0_LUX, AE_RANGE_EV, AE_DARKEN_EV, keyLuminanceNits } from '../lib/photometry.js';
import { purkinjeStrength, type PurkinjeUniforms } from 'enginex/render/color/purkinje.js';
import { heightFogDensity } from '../lib/sky/fog_sky.js';
import { createAces2DisplayNode } from 'enginex/render/color/aces2.js';
import { uniform } from 'three/tsl';
import type { HeightFogState } from './AtmosphereModule.js';
import { projectSunToScreen, setGodRaysCamera, type GodRaysUniforms } from 'enginex/render/gi/GodRaysPass.js';
import { cloudShadowTexture } from '../lib/cloud_shadows.js';
import { CLOUD_FIELD_ROTATION, CLOUD_FIELD_SCALE_M, stage4Stats } from '../lib/stage4_fx.js';
import { Vector3 } from 'three/webgpu';
import { createColorblindFilterNode } from 'enginex/accessibility/colorblind/ColorblindFilterNode.js';
import { sunDirection } from '../lib/lighting.js';
import { bakeLook, LUT_SIZE } from '../lib/lut_looks.js';
import { currentTod } from './SkyModule.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { bus } from '../bus.js';
import { atlas } from '../state.js';
// ── SPACE PROFILE (space agent, 2026-09-25 — report §9 R1; the rules live in lib/space/space_look.ts) ──
import { applySpacePostProfile, SpaceExposure } from '../lib/space/space_post.js';
// ── END SPACE PROFILE ──

/** The displayed key the anchored metering aims at, and each meter's bias — the reading of a frame FULL OF SUNLIT
 *  GROUND over the key (`__exposure()` reports both). RE-TUNED 2026-09-26 against the honest meter (the old 1.0 / 1.8
 *  were fitted to a reading 5 stops low): looking down at the ground the histogram reads 1.19 (WebGL2) / 1.20 (WebGPU)
 *  × the key, the log-average ~1.05; a clear landscape (the bright horizon ring in view) 4.8–5.3 × — which the
 *  darkening limit (AE_DARKEN_EV) leaves to the curve. */
const AE_KEY = 0.18;
/**
 * sky/post (2026-09-27, realism-additions §1.7 — re-derived on the HONEST sky, V 50 km, the visibility-law fog):
 * the histogram band 20–95 % (UE 4.25's direction, 10–90 %) replaces 50–98 %. Measured on WebGPU med at the hub,
 * all five hours (`p2sky/meter5-med-webgpu`, each view's bias-for-zero per statistic): the six non-sun views spread
 * 0.86 EV under 20–95 % against 1.08 EV under 50–98 % (the top band followed the bright horizon ring and the storm's
 * overcast); at bias 0.92 every outdoor correction is inside −0.3 … +0.5 (noon +0.23, the anti-sun sky +0.13, the blue
 * hour −0.05, midnight +0.43, the storm 0.0; the sunset view's −0.43 and the two sun-facing views take the −0.3
 * darkening limit) — and the storm now reads darker than noon (50–98 % at 1.2 put it +0.37 EV over noon: the probe's
 * "storm DARKER than noon" failed at 132 vs 126). 50–98 % at 1.2 also held the band (sunset raw −0.6) — kept as the
 * `__aeMeasure().alt.h50_98` comparison.
 */
const AE_METER_BIAS: Record<'average' | 'histogram', number> = { average: 1.05, histogram: 0.92 };
const AE_HISTOGRAM_BAND = { lowPercentile: 0.2, highPercentile: 0.95 };
/** The 'aces2' operator's pre-gain: the scene exposure at which ACES 2.0 draws an 18 % card (× EXPOSURE_REF) at AgX's grey (sRGB 128). */
const ACES2_GREY_MATCH = 2.434;
/** THE PURKINJE SHIFT's adaptation time constant (s): rods adapt over minutes; 1.5 s keeps a meter reading from ever stepping the colour. */
const PURKINJE_TAU_S = 1.5;
/** THE NIGHT IN-SCATTER's A/B switch (`__nightInscatter(false)` = the old black far haze on the froxel path). */
let nightInscatterOn = true;
(globalThis as Record<string, unknown>).__nightInscatter = (on?: boolean): boolean => { if (typeof on === 'boolean') nightInscatterOn = on; return nightInscatterOn; };

/** `atlas:ao` — 'on' | 'off' | null (the tier decides). */
function aoOverride(): 'on' | 'off' | null {
  try { const v = localStorage.getItem('atlas:ao'); return v === 'on' || v === 'off' ? v : null; } catch { return null; }
}
/** `atlas:ssr-webgl2` = 'off' — the kill switch for SSR on the WebGL2 fallback (gaps wave round 2: it compiles there now). */
function ssrWebGL2Off(): boolean {
  try { return localStorage.getItem('atlas:ssr-webgl2') === 'off'; } catch { return false; }
}
import { cloudsAfterAAFor, isWebGPURenderer } from '../lib/temporal_resolve.js';
import { CutDetector, CUT_EVENTS } from '../lib/history_epoch.js';
import { cloudsAllowed } from '../lib/space_clouds.js';
import { skyglowLive } from '../lib/light_pollution_live.js';
import { streetAdaptationNits } from '../lib/light_pollution.js';
import { type Tier, nextTier, setActiveTier, activeTier, activePreset, effectivePixelRatio } from '../lib/quality_tiers.js';

interface PostRenderer {
  createPostProcessing(scene: THREE.Scene, camera: THREE.Camera, config?: unknown): unknown;
  /** sky/post (2026-09-27): THE REBUILD FRAME — build beside the live pipeline, swap when ready (engine PostPipelineSwap). */
  replacePostProcessing?(scene: THREE.Scene, camera: THREE.Camera, config?: unknown): unknown;
  pendingPostProcessing?: unknown;
  postProcessing: { enabled: boolean; dispose?(): void } | null;
  native: THREE.WebGPURenderer;
}

export class PostModule implements IModule {
  readonly name = 'Post';
  private app!: App;
  private built = false;
  private onKey = (e: KeyboardEvent): void => { if (e.key === 'F4') this.setTier(nextTier(activeTier())); };
  private onResize = (): void => { this.applyPixelRatio(); };
  /** THE HISTORY EPOCH (shader gaps program, 2026-09-29): camera cuts reset the temporal histories (lib/history_epoch.ts). */
  private readonly cuts = new CutDetector();
  private readonly cutLog: Array<{ t: number; reason: string; epoch: number }> = [];
  private readonly cutCam = { p: new Vector3(), q: { x: 0, y: 0, z: 0, w: 1 } };
  private cutUnsubs: Array<() => void> = [];
  private lastSpaceFlight = false;

  init(app: App): void {
    this.app = app;
    this.applyPixelRatio(); // the tier's cap from the first frame (see applyPixelRatio)
    // A resolution TARGET is a ratio of the window's CSS height — re-derive it on resize.
    window.addEventListener('resize', this.onResize);
    // The active graphics settings are loaded in main.ts (loadSettings) BEFORE any module
    // builds, so activePreset() is already correct here — don't reset the tier.
    // Build lazily once the camera exists (the Post phase runs after Render each frame).
    app.world.addSystem(makeSystem('PostBuild', Phase.Post, 10, () => this.ensureBuilt()));
    // Shader-quality sync (2026-08-17 gap-closure): feeds the new pipeline
    // passes their live inputs each frame — fog/capsule sun direction from
    // LightingModule, the player capsule proxy, auto-exposure adaptation, and
    // late-wires the aerial volume once the sky's first froxel bake lands.
    app.world.addSystem(makeSystem('PostShaderSync', Phase.Post, 11, (dt) => this.syncShaderInputs(dt)));
    window.addEventListener('keydown', this.onKey);
    for (const ev of CUT_EVENTS) this.cutUnsubs.push(bus.on(ev, () => { this.cuts.forget(); this.resetHistory(ev); }));
    // THE HISTORY EPOCH lever: reset by hand (a probe's before/after), read the detector and the last cuts.
    (globalThis as unknown as { __historyCut?: (reason?: string) => unknown }).__historyCut = (reason?: string) => {
      if (reason) this.resetHistory(reason);
      return { ...this.cuts.state(), log: this.cutLog.slice(-8), epoch: (this.pipelineFull() as { historyEpoch?: number } | null)?.historyEpoch ?? null };
    };
    // QA lever (coordinator, 2026-09-26): the scene-pass target the auto-exposure meter reads, the controller's
    // last reading and target, the live render scale and the sky pipeline — the qa-exposure probe measures the
    // meter against a direct readback of the SAME target. Read-only.
    (globalThis as unknown as { __postDiag?: () => unknown }).__postDiag = () => {
      const c = this.aeCtl as unknown as { targetStops?: number; correctionStops?: number; meteredLuminance?: number; measuring?: boolean; config?: { correctionRangeEv?: number; metering?: string; strength?: number } } | null;
      // lighting (2026-09-26): the Atlas sky module is named 'AtlasSky' — 'Sky' found nothing and the probe logged null.
      const skyMod = this.app.getModule('AtlasSky') as { _engineSky?: { renderer?: { pipeline?: string } } } | undefined;
      return {
        scenePassRT: this.pipelineFull()?.scenePassRenderTarget ?? null,
        ae: c ? { targetStops: c.targetStops, correctionStops: c.correctionStops, meteredLuminance: c.meteredLuminance, measuring: c.measuring, range: c.config?.correctionRangeEv, metering: c.config?.metering, strength: c.config?.strength } : null,
        renderScale: this.liveRenderScale,
        skyPipeline: skyMod?._engineSky?.renderer?.pipeline ?? null,
        // sky/post (2026-09-27): the rod shift's adaptation and strength; a replacement chain still setting up (THE REBUILD FRAME)
        purkinje: { ...this.purkinjeState },
        pendingRebuild: !!this.renderer().pendingPostProcessing,
        // THE BUILT-PASS MANIFEST (shader gaps program, 2026-09-29): what the pipeline BUILT on this device — the MRT
        // against the colour-attachment budget, each requested stage built or skipped with its reason, graph order
        // with the HDR/display domain, the temporal resolve and the history epoch (engine `PassManifest`).
        manifest: (this.pipelineFull() as { passManifest?: () => unknown } | null)?.passManifest?.() ?? null,
        history: { ...this.cuts.state(), log: this.cutLog.slice(-8) },
      };
    };
    // Calibration lever (lighting, 2026-09-26): take ONE meter reading of the frame as it stands NOW and return it —
    // under SwiftShader a frame is seconds long and the 10-frame cadence left the probe reading the meter of a view it
    // had already turned away from. The same controller, the same regions; only the timing is the probe's.
    // A TWIN controller (same metering, same anchor) so the live one's in-flight guard can never hand back its previous
    // view's reading (the 03:53 WebGPU run: the sunset view returned noon's 0.8604 verbatim); the live exposure is untouched.
    (globalThis as unknown as { __heightFogOverride?: (d: number | null) => number | null }).__heightFogOverride = (d: number | null) => { this.heightFogOverride = d === null || !Number.isFinite(d) ? null : Math.max(0, d); return this.heightFogOverride; };
    // sky/post (2026-09-27): THE AERIAL FRAME's live A/B ('world' | 'screen'; no argument reads it). The bake follows next frame.
    (globalThis as unknown as { __aerialFrame?: (f?: 'screen' | 'world') => unknown }).__aerialFrame = (f?: 'screen' | 'world') => {
      if (f === 'screen' || f === 'world') this.aerialFrame = f;
      const eng = (this.app.getModule('AtlasSky') as { _engineSky?: { renderer?: { lutSet?: { aerialFrame?: string } | null } } } | undefined)?._engineSky;
      let bake: string | null = null; try { bake = eng?.renderer?.lutSet?.aerialFrame ?? null; } catch { bake = null; }
      const au = (this.pipelineFull() as { aerialUniforms?: { worldFrame?: { value: number } } | null } | null)?.aerialUniforms;
      return { frame: this.aerialFrame, bake, composite: au?.worldFrame ? (au.worldFrame.value ? 'world' : 'screen') : null };
    };
    (globalThis as unknown as { __purkinjeOverride?: (k: number | null) => number | null }).__purkinjeOverride = (k: number | null) => { this.purkinjeOverride = k === null || !Number.isFinite(k) ? null : Math.max(0, Math.min(1, k)); return this.purkinjeOverride; };
    (globalThis as unknown as { __aeMeasure?: () => Promise<unknown> }).__aeMeasure = async () => {
      const ctl = this.aeCtl; const rt = this.pipelineFull()?.scenePassRenderTarget ?? null;
      if (!ctl || !rt) return { error: ctl ? 'no scene target' : 'metering off' };
      const twin = new AutoExposureController({ ...ctl.config }, 1);
      const drop = Math.max(0, exposureState.ev100 - exposureState.evScene);
      twin.setAnchor(exposureState.exposureRef * exposureState.compensation, exposureState.meterKey ?? AE_KEY * Math.pow(2, -drop));
      await twin.measure(this.renderer().native, rt);
      // The same frame through the other statistics (calibration: which band meters the ground, not the sky).
      const alt: Record<string, number> = {};
      for (const [k, cfg] of [['avg', { metering: 'average' as const }], ['h50_98', { metering: 'histogram' as const, lowPercentile: 0.5, highPercentile: 0.98 }], ['h25_75', { metering: 'histogram' as const, lowPercentile: 0.25, highPercentile: 0.75 }], ['h10_50', { metering: 'histogram' as const, lowPercentile: 0.1, highPercentile: 0.5 }], ['h20_95', { metering: 'histogram' as const, lowPercentile: 0.2, highPercentile: 0.95 }] /* sky/post (2026-09-27): §1.7's candidate */] as const) {
        const t = new AutoExposureController(cfg, 1); await t.measure(this.renderer().native, rt); alt[k] = t.meteredLuminance;
      }
      return { meteredLum: twin.meteredLuminance, targetStops: twin.targetStops, metering: twin.config.metering, blind: twin.blindReadings, alt };
    };
  }

  private renderer(): PostRenderer { return this.app.renderer as unknown as PostRenderer; }
  private camera(): THREE.Camera | null { try { return this.app.renderer.cameras.main; } catch { return null; } }

  private readonly sunScratch = new Vector3();

  /** Reset every temporal history of the live chain (a replacement still setting up starts fresh anyway). */
  private resetHistory(reason: string): void {
    const pp = this.pipelineFull() as { resetHistory?: (r: string) => number } | null;
    const epoch = pp?.resetHistory?.(reason);
    if (epoch === undefined) return;
    this.cutLog.push({ t: Math.round(performance.now()), reason, epoch });
    if (this.cutLog.length > 32) this.cutLog.splice(0, this.cutLog.length - 32);
  }

  /** THE HISTORY EPOCH: feed the camera's ABSOLUTE pose to the cut detector (render position − the FP64 rebase shift). */
  // PERF (shader sweep C, 2026-10-06): the kill switch was a synchronous localStorage read EVERY frame (a storage lookup
  // + string compare on the frame path) — read at 1 Hz, the rule the adaptive-quality and far-tier opt-outs follow.
  private cutsOff = false;
  private cutsOffReadAt = -Infinity;
  private detectCut(dt: number): void {
    const nowMs = performance.now();
    if (nowMs - this.cutsOffReadAt >= 1000) {
      this.cutsOffReadAt = nowMs;
      try { this.cutsOff = localStorage.getItem('atlas:history-cuts') === 'off'; } catch { this.cutsOff = false; /* storage blocked */ }
    }
    const off = this.cutsOff;
    const cam = this.camera();
    if (off || !cam) return;
    // entering or leaving space flight swaps the whole scene under the camera — a cut by definition
    if (atlas.spaceFlight !== this.lastSpaceFlight) { this.lastSpaceFlight = atlas.spaceFlight; this.cuts.forget(); this.resetHistory(atlas.spaceFlight ? 'space-flight on' : 'space-flight off'); return; }
    const scene = this.app.world.scene as unknown as { position: Vector3 };
    cam.getWorldPosition(this.cutCam.p).sub(scene.position);
    const q = cam.quaternion;
    const v = this.cuts.sample({ x: this.cutCam.p.x, y: this.cutCam.p.y, z: this.cutCam.p.z, qx: q.x, qy: q.y, qz: q.z, qw: q.w, dt, spatial: !atlas.spaceFlight });
    if (v.cut && v.reason) this.resetHistory(v.reason);
  }

  /** applySettings() requests are coalesced here: ONE rebuild per frame with the latest settings. */
  private rebuildPending = false;

  private ensureBuilt(): void {
    // XR (2026-09-28): the chain is bypassed while presenting, and a build compiles its passes — on the Quest 3 the
    // session's entry rebuilt it for nothing, inside the stall that starved the database socket. A pending rebuild
    // WAITS (it stays pending) and runs on the first desktop frame after the session, with the restored settings.
    if (this.renderer().native.xr?.isPresenting) return;
    if (this.rebuildPending) {
      this.rebuildPending = false;
      // sky/post (2026-09-27): THE REBUILD FRAME — never dispose the live chain first: it keeps drawing until the
      // replacement is ready (the raw fallback drew ~3 s of untransformed frames after every apply). build() hands the
      // new config to the engine's replacePostProcessing, which swaps and disposes the old one itself.
      if (!this.renderer().replacePostProcessing) this.renderer().postProcessing?.dispose?.();
      this.built = false;
    }
    if (this.built) return;
    const cam = this.camera();
    if (cam) { this.build(cam); this.built = true; }
  }

  /**
   * The render-scale cap (lib/quality_tiers effectivePixelRatio — ultra alone
   * may exceed the display's devicePixelRatio; every other tier clamps to it,
   * WHY: on a dpr-1.5 display med/high/ultra otherwise all clamp to 1.5 and the
   * ladder stops differentiating). Applied at init and BEFORE every pipeline
   * build: the pipeline measures the drawing buffer at build, and the old
   * apply-after-build left the menu, the streaming world and the pipeline's own
   * size at the uncapped ratio ("fix the render scale cap", 2026-09-08).
   * NEVER while presenting: the XR framebuffer scale is the session's
   * framebufferScaleFactor, and a mid-session pixelRatio write (Quest browser
   * dpr = 0.75!) shrank the render to 3/4 of the layer with overlapping eyes —
   * the 2026-08-16 eye-misalignment root cause. Desktop settings re-apply on
   * session end.
   */
  private applyPixelRatio(): void {
    const r = this.renderer();
    if (r.native.xr?.isPresenting) return;
    const want = effectivePixelRatio();
    if (Math.abs(r.native.getPixelRatio() - want) > 1e-6) r.native.setPixelRatio(want);
  }

  private build(cam: THREE.Camera): void {
    const r = this.renderer();
    this.applyPixelRatio(); // BEFORE createPostProcessing: the pipeline sizes itself from the drawing buffer
    // (aerial wiring is identity-latched below — see aerialWiredVol; the old
    // boolean reset here completed THE HYPERWARP REBUILD LOOP, 2026-08-23)
    // AO OVER WEBGPU (render wave 1, 2026-09-17). GTAO makes the pipeline request a NORMAL
    // MRT target on the scene pass; the July 2026 guard stripped AO here because on Dawn the
    // line geometries (built from points, no `normal` attribute) failed pipeline validation
    // once that target existed, and an invalid pipeline blacked the whole frame. The lines
    // carry a zero normal now (lib/line_geometry.ts), so AO runs on a true WebGPU backend —
    // `localStorage atlas:ao = 'off'` is the kill switch, 'on' forces it. WebGL2 keeps its
    // strip: there a material that writes fewer outputs than the pass has attachments has
    // its DRAW dropped, which is structural, not a missing attribute.
    const preset = activePreset();
    const post: Record<string, unknown> = { ...preset.post };
    // FSR 1 upscale (perf frontier 2026-09-08): the scene pass renders at
    // 1/ratio and EASU + RCAS rebuild the output size before every other pass.
    // Never while presenting in XR (the session's framebuffer scale governs).
    if (preset.upscale && preset.upscale !== 'off' && !r.native.xr?.isPresenting) post.upscale = { quality: preset.upscale, mode: preset.upscaleMode === 'taau' ? 'taau' : 'fsr' };
    // Accessibility: colourblind daltonization as the final display-referred
    // transform (graphics-menu wave — the engine pass existed, unwired).
    if (preset.colorblindMode && preset.colorblindMode !== 'off') {
      try {
        const filt = createColorblindFilterNode(preset.colorblindMode, preset.colorblindStrength ?? 1);
        post.colorblind = { transform: filt.transform };
      } catch { /* engine build without the node — skip */ }
    }
    if (this.isWebGPUBackend()) {
      if (aoOverride() === 'off') delete (post as { ao?: unknown }).ao;
    } else {
      // The MIRROR gotcha: bloom (emissive target) and AO (normal target) make the
      // pipeline request MRT on the scene pass; on the WebGL2 FALLBACK every scene
      // material that doesn't write those extra outputs hits "Active draw buffers
      // with missing fragment shader outputs" and the draw is DROPPED — a black
      // world (only the few MRT-writing materials survive). Default tier is 'high'
      // (bloom+ao on), so every WebGL2 fallback browser rendered nothing until
      // this guard.
      // BLOOM COMES BACK ON WebGL2 (2026-09-25, §10 R5): the strip was for three's EMISSIVE-MRT bloom; the
      // dual-Kawase path reads the scene COLOUR only (no extra attachment), so the fallback keeps it — forced to
      // the Kawase filter (the mip chain is not what was verified here).
      const bl = (post as { bloom?: Record<string, unknown> }).bloom;
      if (bl) (post as { bloom?: unknown }).bloom = { ...bl, mode: 'kawase' };
      delete (post as { ao?: unknown }).ao;
      // REFLECTIONS ON WebGL2 (gaps wave round 2, 2026-09-28): the engine builds SSR on WebGL2 through its vendored node
      // (three's emitted one invalid GLSL line); `atlas:ssr-webgl2 = 'off'` drops it — pass AND its three MRT targets.
      const ssrCfg = (post as { ssr?: Record<string, unknown> }).ssr;
      if (ssrCfg && ssrWebGL2Off()) (post as { ssr?: unknown }).ssr = { ...ssrCfg, webgl2: false };
    }
    // THE POST QUAD'S CAMERA IS NOT THE SCENE'S (2026-09-25): the height fog now reads an EXPLICIT camera
    // (syncShaderInputs writes it) — its "world" ray was the view-space ray, its floor unanchorable.
    if (post.volumetricFog) post.volumetricFog = { ...(post.volumetricFog as object), explicitCamera: true };
    // sky/post (2026-09-27): the capsule shadows reconstruct world points in the same quad — the same explicit camera.
    if (post.capsuleShadows) post.capsuleShadows = { ...(post.capsuleShadows as object), explicitCamera: true };
    // God rays through the cloud GAPS (§8 R6e): the cloud field's coverage map at the ray's cloud-base hit.
    if (post.godRays) post.godRays = { ...(post.godRays as object), cloudOcclusion: { texture: cloudShadowTexture(this.app), worldScale: CLOUD_FIELD_SCALE_M, rotation: CLOUD_FIELD_ROTATION, threshold: 0.4, softness: 0.3 } };
    // Never stack the sharpen pass on RCAS (§10 R11): the FSR stage already sharpens the upscale.
    if ((post as { upscale?: unknown }).upscale) delete (post as { sharpen?: unknown }).sharpen;
    // The tone-map operator 'aces2': the published ACES 2.0 tone scale (Daniele's, the engine's
    // `createAces2DisplayNode`) at the pipeline's display boundary, GREY-MATCHED to AgX (session 2, 2026-09-25): the
    // engine's older Michaelis-Menten node has no toe — black 0.002 came out at 107/255 and an 18 % card at 196/255
    // (the CPU A/B) — and ACES puts 18 % grey at 10 % of the display where AgX puts it at ~22 %; ACES2_GREY_MATCH
    // (+1.28 EV) lands the card on AgX's grey so the A/B compares the curves' SHAPE, not a stop of exposure.
    if (preset.tonemapOperator === 'aces2') post.tonemap = { customNode: createAces2DisplayNode({ preGain: uniform(ACES2_GREY_MATCH), pathToWhite: 0.6 }) };

    // Phase C: composite the volumetric clouds as a DEPTH-AWARE post pass (not in
    // SkyModule.backgroundNode — see SkyModule.ts). The engine PostProcessingPipeline
    // `clouds` hook composites `sceneColor·(1−a)+rgb` over SKY pixels (depth ≈ far)
    // only, so opaque geometry occludes the clouds, and the cloud node ray-marches
    // from the REAL camera position ⇒ world-anchored + parallaxing (the band-pinned-
    // to-the-view bug is fixed). WeatherModule keeps driving the cloud uniforms
    // (coverage / density / tint / night-lighting) wherever the node composites.
    // Guard: CloudModule may be absent in headless/test environments.
    const cloudMod = this.app.getModule('Clouds') as
      | {
          renderer?: { buildCompositeNode(): { rgb: unknown; a: unknown } };
          buildCompositeNode?(): { rgb: unknown; a: unknown };
          lowRenderer?: unknown;
          setQualityPreset?(p: 'low' | 'medium' | 'high' | 'ultra'): void;
          setLowQualityPreset?(p: 'low' | 'medium' | 'high' | 'ultra'): void;
          setParallaxScale?(v: number): void;
          meshFieldActive?: boolean;
          setMeshFieldEnabled?(on: boolean): void;
        }
      | undefined;
    // 2026-08-19: the MESH-cloud field (real 3D volumes in the world) is the
    // primary cloud renderer when configured — the tier's cloudsEnabled flag
    // gates it exactly like the post-pass composite it replaces. When the
    // field is active, the screen-space composite below is SKIPPED (no double
    // clouding); the slab path stays as the fallback for apps without a
    // configured mesh field.
    // NO CLOUDS IN SPACE (2026-09-10): a graphics-menu toggle rebuilds this
    // pipeline mid-flight, and the tier's flag alone re-armed the mesh field
    // (and the composite) over the sector — the sector has no atmosphere.
    // The same rule SpaceModule applies on activate / deactivate.
    const cloudsWanted = cloudsAllowed(!!preset.cloudsEnabled, atlas.spaceFlight);
    cloudMod?.setMeshFieldEnabled?.(cloudsWanted);
    const meshCloudsOn = !!cloudMod?.meshFieldActive;
    if (cloudsWanted && !meshCloudsOn && cloudMod?.renderer) {
      // Cloud march quality follows the graphics tier (the raymarch is the
      // most expensive pass in the chain — ~half the post budget at 'medium').
      // One preset below the tier name: the jittered march start makes each
      // step count look a preset higher, so this trades invisible quality for
      // a large frame-time win. Rebuilding the pipeline (we are inside build())
      // picks up the new step constants.
      // Keys are TIER ids ('med', not 'medium' — the old 'medium' key was dead
      // and med only got its preset via the fallback; an edit to the fallback
      // would have silently changed med-tier cloud quality).
      const marchOverride = preset.cloudMarchQuality ?? 'auto';
      const cloudPreset = marchOverride !== 'auto' ? marchOverride
        : ({ med: 'low', high: 'medium', ultra: 'high' } as const)[
          activeTier() as 'med' | 'high' | 'ultra'
        ] ?? 'low';
      cloudMod.setQualityPreset?.(cloudPreset);
      // Camera-translation response (live uniform — taste knob). Tier default
      // 1.0: world-anchored — clouds are stationary in the world; walk-speed
      // parallax is physically small on the 3.8 km deck, the low puffs make it
      // legible. 0 would freeze the sky to the camera (HDRP camera space).
      cloudMod.setParallaxScale?.(preset.cloudParallax ?? 1);
      // Low scattered layer (walk-speed parallax legibility): the module-level
      // node composites low OVER high. Zero cost when the tier keeps it off —
      // the low march is simply not in the graph.
      const wantLow = preset.lowClouds && !!cloudMod.lowRenderer && !!cloudMod.buildCompositeNode;
      // Near puffs show step/noise grain far more than the distant deck
      // ("pixelated" field report) — run the low layer one preset FINER than
      // the deck: high tier → 'medium', ultra → 'high'. Thin slab ⇒ short
      // in-cloud paths, so the finer stepping stays affordable.
      if (wantLow) cloudMod.setLowQualityPreset?.(activeTier() === 'ultra' ? 'high' : 'medium');
      post.clouds = {
        node: wantLow
          ? cloudMod.buildCompositeNode!()
          : cloudMod.renderer.buildCompositeNode(),
        // Under TRAA the temporal resolve smears the clouds' blue-noise
        // dither and flickers the depth mask at building silhouettes (the
        // blocky-cloud report, 2026-08-17) — composite them AFTER the AA
        // stage instead. Costs bloom on clouds only.
        // gaps wave round 2 (2026-09-28): only where a temporal resolve RUNS — TAAU is WebGPU-only, and on WebGL2 high /
        // ultra (AA-first FSR, no TAAU) `afterAA` put the clouds' linear radiance after the tone map
        // (lib/temporal_resolve.ts; the engine re-checks with cloudsCompositeAfterAA).
        afterAA: cloudsAfterAAFor({
          upscale: (post as { upscale?: unknown }).upscale ? preset.upscale : 'off',
          upscaleMode: preset.upscaleMode,
          traa: !!(post as { traa?: { enabled?: boolean } }).traa?.enabled,
          webgpu: this.isWebGPUBackend(),
          xrPresenting: !!r.native.xr?.isPresenting,
        }),
        // Perf frontier item 6: raymarch at a fraction of the drawing buffer (RTT), composite bilinearly.
        resolutionScale: preset.cloudResolution ?? 1,
      };
    }

    // Aerial-perspective composite: wire the sky's baked froxel volume when
    // the tier wants it AND the volume exists (LUT pipeline + first tier-B
    // bake done). The texture object is identity-stable across re-bakes, so
    // wiring once holds; if the bake hasn't landed yet, syncShaderInputs
    // triggers ONE rebuild when it appears.
    if (preset.aerialEnabled) {
      const vol = this.aerialVolume();
      if (vol) {
        post.aerial = { volume: vol, maxDistanceKm: 32, kmPerWorldUnit: 0.001, frame: this.aerialFrame }; // sky/post (2026-09-27): THE AERIAL FRAME
        this.aerialWiredVol = vol;
      } else {
        this.aerialWiredVol = null; // this pipeline has no aerial — a future volume may re-attempt (bounded)
      }
    } else {
      this.aerialWiredVol = null;
    }

    // The colour LOOK: a procedural 16³ LUT for the engine's lut3D pass (shader wave 2026-09-08).
    if (preset.look && preset.look !== 'none') post.lut3D = { lut: bakeLook(preset.look), size: LUT_SIZE, intensity: 1 };
    else delete post.lut3D;
    // ── SPACE PROFILE (space agent, 2026-09-25): a vacuum's chain — no aerial / fog / god rays / AO / SSGI /
    // SSR / DoF, bloom at 1.0, flares at 4, a final dither. SpaceModule flips it at activate / deactivate only. ──
    if (this.spaceProfile) applySpacePostProfile(post);
    // ── END SPACE PROFILE ──
    // LIGHTING × SPACE (lighting agent 2026-09-25): the tier's bloom is now the energy-conserving LERP veil;
    // the space profile's thresholded glow (threshold 1.0, strength ≥ 0.6) is an ADDITIVE design — at 0.6 a
    // lerp would blur 60 % of the frame. Keep the sector's bloom additive.
    if (this.spaceProfile && post.bloom) post.bloom = { ...(post.bloom as object), composite: 'add' };
    // THE PURKINJE SHIFT (sky/post 2026-09-27, realism-additions §1.6): rods at night — driven by syncShaderInputs,
    // identity by day and off the physical curve; not in the sector (a vacuum's eye is the space profile's).
    if (!this.spaceProfile) post.purkinje = { enabled: true };
    if (r.replacePostProcessing) r.replacePostProcessing(this.app.world.scene, cam, post); // enabled carried over at the swap
    else { r.createPostProcessing(this.app.world.scene, cam, post); if (r.postProcessing) r.postProcessing.enabled = true; }
  }

  /** The froxel-volume IDENTITY the built pipeline carries (null = none). */
  private aerialWiredVol: THREE.Data3DTexture | null = null;
  /** True while the built chain composites the aerial froxels (AtmosphereModule then drops its clear-air FogExp2 term). */
  get aerialLive(): boolean { return this.aerialWiredVol !== null && !this.spaceProfile; }
  /** Late-wiring attempt limiter: at most 3 rebuilds per volume identity,
   *  5 s apart. WHY (the hyperwarp destroyed-buffer flood, forensically
   *  pinned 2026-08-23): the sky re-bakes its froxel volume on its own
   *  cadence; when a rebuild happened to run while the volume was
   *  momentarily null, the old boolean latch stayed false and the next
   *  sync re-triggered — an ENDLESS full post-chain rebuild at the bake
   *  cadence, each cycle destroying every pass's uniform buffers (bloom
   *  et al) while the in-flight frame still referenced them. */
  private aerialAttempt: { vol: THREE.Data3DTexture | null; n: number; at: number } = { vol: null, n: 0, at: 0 };
  /** The anchored metering (2026-09-25): a correction around the curve, applied by LightingModule. */
  private aeCtl: AutoExposureController | null = null;
  private aeFrame = 0;
  /** The load gate / a render hold was up on the last AE frame (lighting, 2026-09-26 — see the meter block). */
  private aeHeld = false;
  /** THE PURKINJE SHIFT (sky/post 2026-09-27): the eased log10 adaptation luminance and what the pass got (`__postDiag().purkinje`). */
  private purkinjeLogL: number | null = null;
  /** The camera's forward for the god rays' behind-the-camera gate (scratch). */
  private readonly godRayFwd = new Vector3();
  private readonly purkinjeState = { k: 0, adaptNits: 0, metered: false, streetNits: 0 };
  /** Probe A/B lever (`__heightFogOverride(density | null)`, sky/post 2026-09-27): pin the height fog's density (the noon-look A/B). */
  private heightFogOverride: number | null = null;
  /**
   * THE AERIAL FRAME (sky/post 2026-09-27; engine AerialPerspectiveLut's WHY): 'world' bakes the haze by the pixel's
   * world direction from the sun and looks its world ray up; 'screen' is the legacy screen-uv lookup (a fixed pyramid —
   * the hills lost their haze when the camera pitched down, the sun's glow sat at screen-right). The bake's frame and
   * the composite's switch follow this each frame; `__aerialFrame(f)` is the live A/B, `atlas:aerial-frame` the default.
   * sky/post (2026-09-27): the default is 'world' now — the aerialab2 A/B/A (med WebGPU, one moment, screen → world →
   * screen) put a grey veil on the NEAR grass in the screen frame only (bottom band 68.8 / 61.6 / 68.6 facing away from
   * a low sun, 80.4 / 72.9 / 79.7 toward it; the two screen shots agree within a level, so it is the frame, not drift),
   * and at a 50 km visibility ground 5–30 m away carries no visible in-scatter; the horizon and a 20° down view read
   * the same in both. `atlas:aerial-frame` = 'screen' is the way back.
   */
  private aerialFrame: 'screen' | 'world' = ((): 'screen' | 'world' => { try { return localStorage.getItem('atlas:aerial-frame') === 'screen' ? 'screen' : 'world'; } catch { return 'world'; } })();
  /** Probe A/B lever (`__purkinjeOverride(k | null)`): pin the strength; null returns it to the adaptation. */
  private purkinjeOverride: number | null = null;

  /** The sky's baked aerial froxel texture, when available. */
  private aerialVolume(): THREE.Data3DTexture | null {
    const sky = this.app.getModule('AtlasSky') as
      | { _engineSky?: { aerialTexture: THREE.Data3DTexture | null } }
      | undefined;
    return sky?._engineSky?.aerialTexture ?? null;
  }

  /** The engine pipeline instance (widened past the local facade type). */
  private pipelineFull(): {
    volumetricFogUniforms?: {
      sunDirection: { value: THREE.Vector3 };
      color: { value: THREE.Vector3 };
      sunColor: { value: THREE.Vector3 };
      sunPower: { value: number }; // the night lobe (2026-10-06) narrows it to the town's dome
      skyOpacity?: { value: number }; // the sky's share (2026-10-06): 0 on a clear night — the stars survive a valley layer
    } | null;
    capsuleShadowsUniforms?: {
      lightDirection: { value: THREE.Vector3 };
      setCapsule(i: number, c: { a: [number, number, number]; b: [number, number, number]; radius: number } | null): void;
    } | null;
    setRenderScale?: (scale: number) => boolean;
    renderScale?: number;
    scenePassRenderTarget?: { width: number; height: number } | null;
  } | null {
    return (this.renderer().postProcessing as ReturnType<PostModule['pipelineFull']>) ?? null;
  }

  /** Per-frame inputs for the new shader passes (Post phase, after build). */
  private syncShaderInputs(dt: number): void {
    if (!this.built) return;
    const preset = activePreset();
    const pp = this.pipelineFull();
    if (!pp) return;
    this.detectCut(dt);

    // God rays (graphics-menu wave): drive the pass's live sun uniforms from
    // the same sun the sky uses — intensity 0 below the horizon.
    {
      const gr = (pp as unknown as { godRays?: { uniforms?: { sunScreenPos: { value: { set(x: number, y: number): void } }; intensity: { value: number } } } }).godRays;
      const cam = this.camera();
      if (gr?.uniforms && cam) {
        const sd = sunDirection(currentTod());
        const proj = projectSunToScreen(this.sunScratch.set(sd.x, sd.y, sd.z), cam);
        gr.uniforms.sunScreenPos.value.set(proj.x, proj.y);
        // The shafts are the KEY's: they fade with it across the horizon and vanish under a storm deck (2026-09-25).
        const m = lightFrame.model;
        // THE SUN BEHIND THE CAMERA (sky/post 2026-09-27, the engine GodRaysModule's gate): with the sun behind the view
        // (clip w < 0) projectSunToScreen clamps it to a screen EDGE and the radial blur threw shafts from that edge;
        // off below alignment 0, faded in over 0 … 0.2 (≈ 78–90° off the view axis — already off-screen) so it never pops.
        const fwd = cam.getWorldDirection(this.godRayFwd);
        const align = fwd.x * sd.x + fwd.y * sd.y + fwd.z * sd.z;
        const facing = Math.min(1, Math.max(0, align / 0.2));
        gr.uniforms.intensity.value = proj.sunBelowHorizon ? 0 : (m ? m.fade * m.tCloud : 1) * facing;
        // …tinted by the key, and blocked where a cloud sits on the path to the sun (R6e — the ground's cloud-shadow rule).
        const gu = gr.uniforms as unknown as GodRaysUniforms;
        if (gu.color && m) gu.color.value.set(m.keyColor[0], m.keyColor[1], m.keyColor[2]);
        if (gu.camPos) {
          setGodRaysCamera(gu, cam);
          const clouds = this.app.getModule('Clouds') as { renderer?: { windOffset?: { x: number; y: number } } } | undefined;
          const wind = clouds?.renderer?.windOffset ?? null;
          const sp = this.app.world.scene.position;
          gu.cloudOffset.value.set((wind?.x ?? 0) - sp.x, (wind?.y ?? 0) - sp.z);
          gu.cloudStrength.value = Math.min(1, stage4Stats.cloudShadowStrength * 1.3);
          gu.cloudBaseY.value = 900 + sp.y;
        }
      }
    }

    // Late aerial wiring: the first froxel bake may land after the pipeline
    // built — rebuild when a NEW volume identity appears, at most 3 attempts
    // per identity, 5 s apart (the loop-proof latch; see aerialAttempt).
    if (preset.aerialEnabled) {
      const vol = this.aerialVolume();
      if (vol && vol !== this.aerialWiredVol) {
        const a = this.aerialAttempt;
        const nowMs = performance.now();
        if (a.vol !== vol) { a.vol = vol; a.n = 0; }
        if (a.n < 3 && nowMs - a.at > 5000) {
          a.n++;
          a.at = nowMs;
          // Rebuild only: applySettings() also emits atlas:quality-changed,
          // which makes StreamingModule evict EVERY warm asset.
          this.rebuildPending = true;
          return;
        }
      }
    }

    // Sun direction for fog + capsule shadows (LightingModule's sun position
    // IS the direction — it targets the origin).
    const lighting = this.app.getModule('Lighting') as
      | { sunLight?: { position: THREE.Vector3 } }
      | undefined;
    const sunPos = lighting?.sunLight?.position;
    if (sunPos && sunPos.lengthSq() > 1e-6) {
      pp.volumetricFogUniforms?.sunDirection.value.copy(sunPos).normalize();
      pp.capsuleShadowsUniforms?.lightDirection.value.copy(sunPos).normalize();
    }
    // CAPSULE SHADOWS UNDER A DECK (2026-09-25, R12): with no direct sun the capsule's occlusion is the only
    // grounding cue — it turns to the SKY (straight up, a soft contact shadow) as the deck closes or the key
    // sets, and softens its penumbra with it.
    {
      const cs = pp.capsuleShadowsUniforms as (NonNullable<typeof pp.capsuleShadowsUniforms> & { penumbra?: { value: number } }) | null | undefined;
      const lm = lightFrame.model;
      if (cs && lm) {
        const sky = Math.max(lm.cloudiness, 1 - lm.fade);
        const ld = cs.lightDirection.value;
        if (sky > 0.001) { ld.x *= 1 - sky; ld.z *= 1 - sky; ld.y = ld.y * (1 - sky) + sky; ld.normalize(); }
        if (cs.penumbra) cs.penumbra.value = 0.25 + 0.45 * sky; // the engine default 0.25 under a clear sun
      }
    }

    // Volumetric fog COLOUR tracks the atmosphere (2026-09-14). Nothing drove
    // it, so the pass sat at the engine default blue-grey [0.65,0.72,0.82] at
    // every hour — a daylight haze colour hanging over midnight and sunset,
    // fighting the analytic FogExp2 that AtmosphereModule does drive. Same
    // source as that fog, so the two layers agree by construction.
    const vf = pp.volumetricFogUniforms;
    // The explicit scene camera for the height fog (see the build's WHY).
    {
      const cam = this.camera();
      const vfc = vf as unknown as { cameraPos?: { value: THREE.Vector3 }; cameraWorld?: { value: THREE.Matrix4 } } | null | undefined;
      if (cam && vfc?.cameraPos && vfc.cameraWorld) { vfc.cameraPos.value.setFromMatrixPosition(cam.matrixWorld); vfc.cameraWorld.value.copy(cam.matrixWorld); }
      // sky/post (2026-09-27): the capsule shadows' explicit camera (the same quad-camera trap as the fog).
      const csc = pp.capsuleShadowsUniforms as unknown as { cameraPos?: { value: THREE.Vector3 }; cameraWorld?: { value: THREE.Matrix4 } } | null | undefined;
      if (cam && csc?.cameraPos && csc.cameraWorld) { csc.cameraPos.value.setFromMatrixPosition(cam.matrixWorld); csc.cameraWorld.value.copy(cam.matrixWorld); }
    }
    // sky/post (2026-09-27): THE AERIAL FRAME — the world lookup's camera + sun, the live switch, and the bake's frame.
    {
      const au = (pp as { aerialUniforms?: { cameraWorld?: { value: THREE.Matrix4 }; sunDirection?: { value: THREE.Vector3 }; worldFrame?: { value: number } } | null }).aerialUniforms;
      const cam = this.camera();
      if (au?.cameraWorld && cam) au.cameraWorld.value.copy(cam.matrixWorld);
      if (au?.worldFrame) au.worldFrame.value = this.aerialFrame === 'world' ? 1 : 0;
      const sunL = (this.app.getModule('Lighting') as { sunLight?: { position: THREE.Vector3 } } | undefined)?.sunLight?.position;
      if (au?.sunDirection && sunL && sunL.lengthSq() > 1e-6) au.sunDirection.value.copy(sunL).normalize();
      const eng = (this.app.getModule('AtlasSky') as { _engineSky?: { renderer?: { lutSet?: { aerialFrame?: string; setAerialFrame?(f: 'screen' | 'world'): void } | null }; requestEnvBake?(): void } } | undefined)?._engineSky;
      try { const ls = eng?.renderer?.lutSet; if (ls?.setAerialFrame && ls.aerialFrame !== this.aerialFrame) { ls.setAerialFrame(this.aerialFrame); eng?.requestEnvBake?.(); } } catch { /* the sky not built yet */ }
    }
    if (vf) {
      const fog = this.app.world.scene.fog as THREE.FogExp2 | THREE.Fog | null;
      if (fog?.color) vf.color.value.set(fog.color.r, fog.color.g, fog.color.b);
      // In-scatter takes the sun's own colour (warm at low elevation), not the
      // fog body colour — otherwise sunset shafts read grey.
      const sunCol = (lighting as { sunLight?: { color?: THREE.Color } } | undefined)?.sunLight?.color;
      if (sunCol) vf.sunColor.value.set(sunCol.r, sunCol.g, sunCol.b);
    }

    // Player capsule proxy (slot 0) — grounds the avatar on tiers without CSM.
    if (pp.capsuleShadowsUniforms) {
      // Render space: the pass reconstructs points from the REBASED camera.
      const sp = this.app.world.scene.position;
      const x = atlas.localX + sp.x; const y = atlas.localY + sp.y; const z = atlas.localZ + sp.z;
      if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
        pp.capsuleShadowsUniforms.setCapsule(0, {
          a: [x, y + 0.35, z], b: [x, y + 1.45, z], radius: 0.38,
        });
      }
    }

    // ── SPACE PROFILE (space agent, 2026-09-25): the sector meters its own eye — histogram, clamped −2 EV …
    // +¼ EV (a black void must never meter UP into a grey veil), biased down by a visible sun. The tier's
    // adaptation (if any) is paused for the flight and resumes on landing. ──
    if (this.spaceProfile) {
      // space (2026-09-27): no reading while the world transition holds the frame (THE BOOT READING, as the ground eye below)
      const spaceHeld = atlas.loadingGate || !!(this.app.renderer as unknown as { paused?: boolean }).paused;
      this.spaceExposure.tick(dt, this.renderer().native, () => this.pipelineFull()?.scenePassRenderTarget ?? null, this.app.world, spaceHeld);
    } else if (this.spaceExposure.running) this.spaceExposure.stop(this.renderer().native);
    // ── END SPACE PROFILE ──
    // Auto exposure, ANCHORED (2026-09-25, lib/photometry — realism §8 R3 P1 / §10 R2): the metering is a
    // CORRECTION of ±AE_RANGE_EV around the time-of-day curve, eased in log space (τ 0.4 s brighter / 1.2 s
    // darker), never free-running — caves and interiors adapt, a night is never lifted to grey day. The key it
    // meters toward is the curve's own displayed key (0.18 · 2^−drop), so the night compensation holds. This
    // module only METERS: LightingModule is the one writer of toneMappingExposure (ref × 2^correction).
    if (preset.autoExposure && exposureState.mode === 'physical' && !this.spaceProfile /* SPACE PROFILE: the sector's eye owns exposure in flight */ && !atlas.spaceFlight) {
      const native = this.renderer().native;
      // THE BOOT READING (lighting, 2026-09-26): under the load screen or a world-transition hold the scene target holds
      // a half-built frame (measured on WebGPU: the SAME 0.0226 after every page load, 5.6 stops under a real noon
      // reading of 1.13) and the anchored correction pegged at +1.5 EV for the first seconds in the world — a whiteout
      // at every entry. No reading while held; the controller starts FRESH (neutral) when the hold lifts.
      const held = atlas.loadingGate || !!(this.app.renderer as unknown as { paused?: boolean }).paused;
      if (held !== this.aeHeld) { this.aeHeld = held; if (!held) this.aeCtl = null; }
      if (!this.aeCtl) this.aeCtl = new AutoExposureController({ correctionRangeEv: AE_RANGE_EV, brightenTau: 0.4, darkenTau: 1.2, ...AE_HISTOGRAM_BAND }, 1);
      const ctl = this.aeCtl;
      ctl.config.correctionRangeEv = preset.autoExposureRangeEv ?? AE_RANGE_EV;
      ctl.config.correctionDarkenEv = Math.min(AE_DARKEN_EV, ctl.config.correctionRangeEv); // lighting (2026-09-26): brighten the dark, barely darken the bright
      const meter = preset.autoExposureMetering ?? 'average';
      ctl.setStrength(preset.autoExposureStrength ?? 1);
      ctl.setMetering(meter);
      const drop = Math.max(0, exposureState.ev100 - exposureState.evScene);
      ctl.setAnchor(exposureState.exposureRef * exposureState.compensation, AE_KEY * Math.pow(2, -drop) * AE_METER_BIAS[meter]);
      if (!held && ++this.aeFrame % 10 === 0 && !native.xr?.isPresenting) {
        const rt = this.pipelineFull()?.scenePassRenderTarget ?? null;
        if (rt) void ctl.measure(native, rt);
      }
      exposureState.correctionEv = ctl.updateAnchored(dt);
      exposureState.metering = meter;
      exposureState.meteredLum = (ctl as unknown as { meteredLuminance?: number }).meteredLuminance;
      exposureState.meterKey = AE_KEY * Math.pow(2, -drop) * AE_METER_BIAS[meter];
      exposureState.darkenEv = ctl.config.correctionDarkenEv;
    } else if (this.aeCtl || exposureState.correctionEv !== 0) {
      this.aeCtl = null;
      exposureState.correctionEv = 0;
      exposureState.metering = 'off';
    }

    // THE VALLEY MIST (2026-09-25, R5): the height fog's floor on the local terrain floor (AtmosphereModule
    // samples it in ABSOLUTE Y; the pass measures RENDER Y — fold scene.position in every frame, the rebase
    // moves it), its density by the mist, and fog boxes in the hollows while the mist is up.
    if (vf) {
      const atmo = this.app.getModule('Atmosphere') as { heightFog?: HeightFogState } | undefined;
      const hf = atmo?.heightFog;
      const base = preset.post.volumetricFog?.density;
      if (hf && base !== undefined) {
        const sp = this.app.world.scene.position;
        (vf as unknown as { baseHeight: { value: number } }).baseHeight.value = hf.floorAbsY + sp.y;
        (vf as unknown as { density: { value: number } }).density.value = this.heightFogOverride ?? heightFogDensity(base, hf.mist);
        const setVol = (vf as unknown as { setVolume?: (i: number, v: { center: [number, number, number]; halfExtents: [number, number, number]; density: number } | null) => void }).setVolume;
        if (setVol) {
          for (let i = 0; i < 3; i++) {
            const h = hf.hollows[i];
            if (h && hf.mist > 0.3) setVol.call(vf, i, { center: [h.x + sp.x, h.y + 8 + sp.y, h.z + sp.z], halfExtents: [110, 9, 110], density: 0.012 * hf.mist });
            else setVol.call(vf, i, null);
          }
        }
      }
    }

    // THE PURKINJE SHIFT (sky/post 2026-09-27): k from the METERED adaptation luminance (the frame's reading in cd/m² —
    // a dark cave at noon goes mesopic too), the curve's key when the meter is off; eased in log (τ PURKINJE_TAU_S) so a
    // meter reading never steps the colour. The per-pixel cone mask pivots on an 18 % card under the frame's light.
    {
      const pu = (pp as { purkinjeUniforms?: PurkinjeUniforms | null }).purkinjeUniforms;
      const lm = lightFrame.model;
      if (pu && lm) {
        const physical = exposureState.mode === 'physical' && !this.spaceProfile && !atlas.spaceFlight;
        const metered = exposureState.metering !== 'off' && (exposureState.meteredLum ?? 0) > 0 ? (exposureState.meteredLum as number) / Math.max(1e-9, exposureState.luxToScene) : 0;
        // THE MESOPIC STREET (2026-10-06): a town's own light keeps the eye mesopic — its lit streets add to what the
        // eye adapts to (light_pollution.streetAdaptationNits), so an amber city sky no longer turns rod blue-grey.
        const street = skyglowLive().active ? streetAdaptationNits(skyglowLive().glow.ratio) : 0;
        const adapt = (metered > 0 ? metered : keyLuminanceNits()) + street;
        const la = Math.log10(Math.max(1e-6, adapt));
        // THE NaN GUARD (sky/post 2026-09-27): a non-finite reading (a NaN meter, a 0 × Inf curve at boot) would latch
        // the eased log forever (NaN + anything = NaN) — skip it; the uniforms only ever take finite values.
        if (Number.isFinite(la)) this.purkinjeLogL = this.purkinjeLogL === null || !Number.isFinite(this.purkinjeLogL) ? la : this.purkinjeLogL + (la - this.purkinjeLogL) * (1 - Math.exp(-Math.max(0, dt) / PURKINJE_TAU_S));
        const kRaw = this.purkinjeOverride ?? (physical && this.purkinjeLogL !== null ? purkinjeStrength(Math.pow(10, this.purkinjeLogL)) : 0);
        const k = Number.isFinite(kRaw) ? kRaw : 0;
        pu.strength.value = k;
        const keyLevel = (0.18 * lm.globalLux * exposureState.luxToScene) / Math.PI;
        if (Number.isFinite(keyLevel)) pu.keyLevel.value = keyLevel;
        this.purkinjeState.k = k; this.purkinjeState.adaptNits = this.purkinjeLogL === null ? 0 : Math.pow(10, this.purkinjeLogL); this.purkinjeState.metered = metered > 0; this.purkinjeState.streetNits = street;
      }
    }

    // THE PHYSICAL LIGHT'S POST INPUTS (2026-09-25): the aerial froxels bake per unit E0 now (the sky's
    // sunIlluminance 1) — carry E0 × the curve here, as the sky dome does; the height fog's sun lobe is the
    // sky's own horizon radiance toward the sun (a radiance, not a hue) so the fog glows like the sky behind it.
    if (lightFrame.model && !this.spaceProfile) {
      const e0s = E0_LUX * exposureState.luxToScene;
      const au = (pp as { aerialUniforms?: { inscatterScale?: { value: number }; nightInscatter?: { value: THREE.Vector3 } } | null }).aerialUniforms;
      if (au?.inscatterScale) au.inscatterScale.value = e0s;
      // THE NIGHT IN-SCATTER (2026-10-06): the froxels bake the sun's in-scatter only — the night horizon fills the
      // haze at night (AtmosphereModule.nightAerial, the clear haze's own colour), or a far ridge fades to black.
      if (au?.nightInscatter) {
        const na = (this.app.getModule('Atmosphere') as { nightAerial?: THREE.Vector3 } | undefined)?.nightAerial;
        if (nightInscatterOn && na && Number.isFinite(na.x + na.y + na.z)) au.nightInscatter.value.copy(na); else au.nightInscatter.value.set(0, 0, 0);
      }
      if (vf) {
        const hs = lightFrame.horizonSun, f = lightFrame.model.fade * lightFrame.model.tCloud;
        vf.sunColor.value.set(hs.x * e0s * f, hs.y * e0s * f, hs.z * e0s * f);
        // THE TOWN'S GLOW IN THE MIST (the night-sky realism wave, 2026-10-06): with the sun's lobe out (fade 0) the lobe
        // turns to the strongest light dome (AtmosphereModule.nightLobe — the fog colour + the dome's excess, as narrow as
        // the dome); the tier's own sun power returns with the sun.
        const atm = this.app.getModule('Atmosphere') as { nightLobe?: { on: boolean; dir: THREE.Vector3; color: THREE.Color; power: number }; skyFogOpacity?: number } | undefined;
        const nl = atm?.nightLobe;
        if (nl?.on && lightFrame.model.fade <= 0) {
          vf.sunDirection.value.copy(nl.dir); vf.sunColor.value.set(nl.color.r, nl.color.g, nl.color.b); vf.sunPower.value = nl.power;
        } else vf.sunPower.value = activePreset().post.volumetricFog?.sunPower ?? 8;
        // THE SKY'S SHARE (2026-10-06): a clear night leaves the sky to the sky shader (AtmosphereModule.skyFogOpacity — 1 by day).
        if (vf.skyOpacity) { const so = atm?.skyFogOpacity; vf.skyOpacity.value = typeof so === 'number' && Number.isFinite(so) ? Math.max(0, Math.min(1, so)) : 1; }
      }
    }
  }

  /** True on the real WebGPU (Dawn) backend; false when the WebGPURenderer falls back to WebGL2. */
  private isWebGPUBackend(): boolean {
    // By the PROPERTY three sets, not the class name: a production build minifies `WebGPUBackend` (the shipped bundle
    // calls it `RF`), and the name test read WebGL2 on every WebGPU page there — AO stripped, bloom forced to Kawase
    // (gaps wave round 2, found reading the built bundle).
    return isWebGPURenderer(this.renderer().native);
  }

  /** Switch to a clean quality preset (clears per-feature customisation), then apply. */
  setTier(tier: Tier): void {
    setActiveTier(tier);
    this.applySettings();
  }

  /**
   * Rebuild the post pipeline from the LIVE graphics settings + re-scale the rest of the
   * pipeline. Call after a preset change OR any per-feature change (the Esc graphics menu
   * does, via activePreset()'s setters). TSL is statically compiled per config, so a change
   * requires a pipeline rebuild.
   */
  applySettings(): void {
    // Coalesced (perf sweep 2026-08-22): the boot sequence called this from the
    // tier load, the VR optimizer, the main menu and the aerial-volume wiring —
    // seven synchronous pipeline builds (each compiles its passes). Now the
    // PostBuild system rebuilds ONCE on the next Post phase with the final settings.
    this.rebuildPending = true;
    bus.emit('atlas:quality-changed', {}); // G5: Lighting/Culling/Streaming rescale from activePreset()
    bus.emit('atlas:environment-updated', {}); // re-apply fog/weather with the new scales
  }

  /**
   * Move the FSR render scale LIVE (the adaptive governor's lever) — no
   * rebuild. False when the built pipeline has no upscale stage.
   */
  setRenderScaleLive(scale: number): boolean {
    const pp = this.pipelineFull();
    return pp?.setRenderScale ? pp.setRenderScale(scale) : false;
  }
  /** The upscale ratio the built pipeline is running (1 = native). */
  get liveRenderScale(): number { return this.pipelineFull()?.renderScale ?? 1; }

  // ── SPACE PROFILE (space agent, 2026-09-25 — report §9 R1) ──
  /** True while the sector's vacuum chain is (to be) built. */
  private spaceProfile = false;
  private readonly spaceExposure = new SpaceExposure();
  /**
   * The sector's post profile: SpaceModule calls this from activate() / deactivate() ONLY (never on a system
   * swap — a hyperwarp stays in space and must not rebuild). One coalesced rebuild on the next Post phase,
   * under the launch / landing transition cover.
   */
  setSpaceProfile(on: boolean): void {
    if (this.spaceProfile === on) return;
    this.spaceProfile = on;
    this.rebuildPending = true;
  }
  get spaceProfileOn(): boolean { return this.spaceProfile; }
  get spaceExposureStats(): { exposure: number; adaptation: number; target: number; bias: number } { return this.spaceExposure.stats; }
  // ── END SPACE PROFILE ──

  /** Test/inspection hooks. */
  get currentTier(): Tier { return activeTier(); }
  get pipeline(): { enabled: boolean } | null { return this.renderer().postProcessing; }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    for (const u of this.cutUnsubs) u();
    this.cutUnsubs = [];
    window.removeEventListener('resize', this.onResize);
    this.aeCtl = null;
    exposureState.correctionEv = 0;
    this.renderer().postProcessing?.dispose?.();
  }
}
